<div class="xwai-chat">
  <!-- ===== Header ===== -->
  <div class="chat-header">
    <div class="chat-title">
      @if (is_viewing_saved_conversation) {
        <span nz-icon nzType="history" nzTheme="outline" class="chat-title-icon"></span>
      } @else {
        <span nz-icon nzType="message" nzTheme="outline" class="chat-title-icon"></span>
      }
      <span class="chat-title-text">{{ chatTitle }}</span>
      @if (is_viewing_saved_conversation) {
        <span class="chat-title-badge">Saved</span>
      }
    </div>

    <div class="chat-header-actions">
      @if (is_viewing_saved_conversation) {
        <button class="header-btn header-btn--primary" (click)="backToLiveChat()">
          <span nz-icon nzType="rollback" nzTheme="outline"></span>
          <span class="header-btn-label">Back to live chat</span>
        </button>
      } @else {
        <button class="header-btn" (click)="startNewChat()" [disabled]="thread_loading || answer_loading"
          nz-tooltip nzTooltipTitle="Start a fresh conversation">
          <span nz-icon nzType="plus" nzTheme="outline"></span>
          <span class="header-btn-label">New chat</span>
        </button>
        <button class="header-btn" (click)="openSaveConversationMenu()" [disabled]="messages.length === 0"
          nz-tooltip nzTooltipTitle="Save this conversation">
          <span nz-icon nzType="save" nzTheme="outline"></span>
          <span class="header-btn-label">Save</span>
        </button>
      }
      <button class="header-btn header-btn--icon" nz-dropdown [nzDropdownMenu]="chatMenu" nzPlacement="bottomRight"
        [disabled]="messages.length === 0">
        <span nz-icon nzType="more" nzTheme="outline"></span>
      </button>
      <nz-dropdown-menu #chatMenu="nzDropdownMenu">
        <ul nz-menu>
          <li nz-menu-item (click)="exportMarkdown()">
            <span nz-icon nzType="file-markdown" nzTheme="outline" class="menu-icon"></span>
            Export as Markdown
          </li>
          <li nz-menu-item (click)="downloadChatObject()">
            <span nz-icon nzType="download" nzTheme="outline" class="menu-icon"></span>
            Download as JSON
          </li>
        </ul>
      </nz-dropdown-menu>
    </div>
  </div>

  <!-- ===== Messages ===== -->
  <div class="chat-body">
    <div class="chat-scroll" #chatScroll (scroll)="onChatScroll()">
      <div class="messages-column" (click)="onMessagesClick($event)">
        @if (showEmptyHero) {
          <div class="empty-hero" [@fadeInOut]>
            <img src="/assets/icons/xwai-icon-cropped.png" alt="XWAI" class="hero-logo" />
            <h2 class="hero-title">How can I help you today?</h2>
            <p class="hero-subtitle">
              Ask about your projects, jobs, model slices, shot data, and documentation.
            </p>
            <div class="starter-prompts">
              @for (prompt of starter_prompts; track prompt) {
                <button class="starter-prompt" (click)="usePrompt(prompt)" [disabled]="isInputDisabled">
                  {{ prompt }}
                </button>
              }
            </div>
          </div>
        }

        @for (msg of messages; track msg; let i = $index) {
          <div #allTheseThings [@slideIn]
            class="chat-msg msg"
            [class.msg--user]="msg.role === 'user'"
            [class.msg--assistant]="msg.role !== 'user'">

            @if (msg.role !== 'user') {
              <div class="msg-avatar">
                <img src="/assets/icons/xwai-icon-cropped.png" alt="XWAI" />
              </div>
            }

            <div class="msg-body">
              @if (msg.role == 'user') {
                <div class="msg-bubble">
                  <p>{{ msg.content }}</p>
                </div>
              }

              @if (msg.role == 'xwi-gpt') {
                @if (msg.streaming) {
                  @if (streaming_status && !msg.content) {
                    <!-- tool-progress ("blue bar") shown until the answer text starts -->
                    <div class="streaming-status">
                      <span nz-icon nzType="loading" nzTheme="outline"></span>
                      {{ streaming_status }}
                    </div>
                  } @else {
                    <!-- render markdown live as tokens arrive (getHtml is memoised
                         per-string; partial markdown renders gracefully). The
                         blinking caret lives on the container via CSS so it never
                         leaks a stray block into the parsed HTML. -->
                    <div class="message-content is-streaming" [innerHTML]="getHtml(msg.content)"></div>
                  }
                } @else if (msg.drive_images && msg.drive_images.length > 0) {
                  <div class="message-content-wrapper">
                    <div class="message-content" [innerHTML]="getHtml(msg.content)"></div>
                    <div class="drive-image-container">
                      @for (img of msg.drive_images; track img) {
                        @if (img) {
                          <img (click)="openGoogleDriveImage(img)" [src]="img" alt="Google Drive Image"
                            class="drive-image-thumbnail">
                        }
                      }
                    </div>
                  </div>
                } @else {
                  <div class="message-content" [innerHTML]="getHtml(msg.content)"></div>
                }

                <!-- "How I got this" — the tools/SQL the agent ran -->
                @if (!msg.streaming && msg.steps && msg.steps.length > 0) {
                  <div class="agent-steps">
                    <span class="agent-steps-toggle" (click)="msg.showSteps = !msg.showSteps">
                      <span nz-icon [nzType]="msg.showSteps ? 'down' : 'right'" nzTheme="outline"></span>
                      How I got this ({{ msg.steps.length }} {{ msg.steps.length === 1 ? 'step' : 'steps' }})
                    </span>
                    @if (msg.showSteps) {
                      <ol class="agent-steps-list">
                        @for (step of msg.steps; track $index) {
                          <li>
                            <span class="agent-step-tool">{{ stepLabel(step) }}</span>
                            @if (stepDetail(step)) {
                              <code class="agent-step-detail">{{ stepDetail(step) }}</code>
                            }
                          </li>
                        }
                      </ol>
                    }
                  </div>
                }
              }

              @if (msg.role == 'slice-image') {
                <div>
                  @for (link of msg.content.image_links; track j; let j = $index) {
                    <ng-container *ngTemplateOutlet="sliceImageVisualizer; context: {
                                  content: {
                                    image_link: link,
                                    image_information: msg.content.image_information[j]
                                  },
                                  message: msg,
                                  index: j
                                }">
                    </ng-container>
                  }
                </div>
              }

              @if (msg.role == 'gif-image') {
                <div>
                  @if (msg.content.images_loading) {
                    <div class="frames-loading">
                      <nz-spin nzSimple></nz-spin> Loading frames for animation...
                    </div>
                  }
                  @if (msg.content.local_image_urls) {
                    <div class="image-flipper-container">
                      <img [src]="msg.content.local_image_urls[msg.content.current_image_index]"
                        alt="Model Evolution Flipper">
                      <div class="flipper-info">
                        Frame {{ msg.content.current_image_index + 1 }} of {{ msg.content.local_image_urls.length }}
                      </div>
                    </div>
                  }
                  @if (msg.content.error) {
                    <div class="error-text">{{ msg.content.error }}</div>
                  }
                </div>
              }

              @if (msg.role == 'shot-image') {
                <div>
                  @for (link of msg.content.image_links; track j; let j = $index) {
                    <ng-container *ngTemplateOutlet="shotImageVisualizer; context: {
                                  content: {
                                    image_link: link,
                                    image_information: msg.content.image_information[j],
                                    request_body: msg.content.request_body[j]
                                  },
                                  message: msg,
                                  index: j
                                }">
                    </ng-container>
                  }
                </div>
              }

              @if (msg.role == 'rag') {
                <ng-container *ngTemplateOutlet="ragResultVisualizer; context: msg"></ng-container>
              }

              @if (msg.role == 'chart') {
                <app-chart-message [content]="msg.content"></app-chart-message>
              }

              @if (msg.role == 'links') {
                <div class="dashboard-links">
                  @if (msg.content.title) {
                    <div class="dashboard-links-title">{{ msg.content.title }}</div>
                  }
                  @if (msg.content.links?.length) {
                    <div class="dashboard-links-row">
                      @for (link of msg.content.links; track link.url) {
                        <a class="dashboard-link-card" [href]="link.url" target="_blank" rel="noopener noreferrer">
                          <span nz-icon [nzType]="link.icon || 'link'" nzTheme="outline" class="dashboard-link-icon"></span>
                          <span class="dashboard-link-body">
                            <span class="dashboard-link-label">{{ link.label }}</span>
                            @if (link.description) {
                              <span class="dashboard-link-desc">{{ link.description }}</span>
                            }
                          </span>
                          <span nz-icon nzType="arrow-right" nzTheme="outline" class="dashboard-link-arrow"></span>
                        </a>
                      }
                    </div>
                  } @else {
                    <div class="dashboard-links-empty">{{ msg.content.note || 'No dashboard links available.' }}</div>
                  }
                </div>
              }

              <div class="msg-meta">
                @if (msg.timestamp) {
                  <span class="msg-time">{{ msg.timestamp | date:'shortTime' }}</span>
                }
                <div class="msg-actions">
                  @if (isCopyable(msg)) {
                    <button class="msg-action" (click)="copyMessage(msg, i)"
                      nz-tooltip [nzTooltipTitle]="copied_index === i ? 'Copied!' : 'Copy'">
                      <span nz-icon [nzType]="copied_index === i ? 'check' : 'copy'" nzTheme="outline"></span>
                    </button>
                  }
                  @if (msg.role === 'user' && !is_viewing_saved_conversation) {
                    <button class="msg-action" (click)="editAndResend(msg)"
                      nz-tooltip nzTooltipTitle="Edit and resend">
                      <span nz-icon nzType="edit" nzTheme="outline"></span>
                    </button>
                  }
                  @if (msg.role !== 'user' && isLastMessage(i) && !is_viewing_saved_conversation) {
                    <button class="msg-action" (click)="regenerateLast()" [disabled]="answer_loading"
                      nz-tooltip nzTooltipTitle="Regenerate answer">
                      <span nz-icon nzType="reload" nzTheme="outline"></span>
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        @if (showTypingIndicator) {
          <div class="chat-msg msg msg--assistant" [@fadeInOut]>
            <div class="msg-avatar">
              <img src="/assets/icons/xwai-icon-cropped.png" alt="XWAI" />
            </div>
            <div class="msg-body">
              @if (streaming_status) {
                <div class="streaming-status">
                  <span nz-icon nzType="loading" nzTheme="outline"></span>
                  {{ streaming_status }}
                </div>
              } @else {
                <div class="typing-indicator">
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>

    @if (show_scroll_fab) {
      <button class="scroll-fab" (click)="scroll_to_last()" [@fadeInOut]>
        <span nz-icon nzType="down" nzTheme="outline"></span>
      </button>
    }
  </div>

  <!-- ===== Context-aware quick prompts ===== -->
  @if (!thread_loading && !is_viewing_saved_conversation && hasPageContext && quickPrompts.length > 0) {
    <div class="quick-prompts" [@fadeInOut]>
      <span class="quick-prompts-label">
        <span nz-icon nzType="thunderbolt" nzTheme="fill"></span>
        About {{ contextLabel }}:
      </span>
      @for (qp of quickPrompts; track qp.label) {
        <button class="quick-prompt-chip" (click)="useQuickPrompt(qp.prompt)" [disabled]="isInputDisabled">
          {{ qp.label }}
        </button>
      }
    </div>
  }

  <!-- ===== Composer ===== -->
  @if (!thread_loading) {
    <div class="composer">
      <div class="composer-card">
        <textarea
          #messageInput
          [placeholder]="inputPlaceholder"
          [disabled]="isInputDisabled"
          [(ngModel)]="question"
          (input)="autoResizeTextarea($event)"
          (keydown.enter)="handleEnterKey($event)"
          rows="1"></textarea>
        @if (answer_loading) {
          <button class="send-btn stop-btn" (click)="stopStreaming()"
            nz-tooltip nzTooltipTitle="Stop generating">
            <span nz-icon nzType="pause" nzTheme="outline"></span>
          </button>
        } @else {
          <button class="send-btn" [disabled]="isInputDisabled || !question.trim()" (click)="prompt_agent()"
            nz-tooltip nzTooltipTitle="Send (Enter)">
            <span nz-icon nzType="arrow-up" nzTheme="outline"></span>
          </button>
        }
      </div>
      <div class="composer-hint">XWAI can make mistakes — verify important results.</div>
    </div>
  }

  @if (thread_loading) {
    <div class="composer">
      <div class="composer-card composer-card--skeleton">
        <div class="skeleton-input-line"></div>
        <div class="skeleton-button-circle"></div>
      </div>
    </div>
  }
</div>

<!-- ===== Maximised-table overlay ===== -->
@if (expandedTableHtml) {
  <div class="mk-table-overlay" (click)="closeExpandedTable()" [@fadeInOut]>
    <div class="mk-table-overlay-card" (click)="$event.stopPropagation()">
      <div class="mk-table-overlay-header">
        <span>Table</span>
        <div class="mk-table-overlay-actions">
          <button class="mk-table-overlay-btn" (click)="copyExpandedCsv()">
            <span nz-icon nzType="copy" nzTheme="outline"></span> Copy CSV
          </button>
          <button class="mk-table-overlay-btn" (click)="downloadExpandedCsv()">
            <span nz-icon nzType="download" nzTheme="outline"></span> Download CSV
          </button>
          <button class="mk-table-overlay-close" (click)="closeExpandedTable()"
            nz-tooltip nzTooltipTitle="Close">
            <span nz-icon nzType="close" nzTheme="outline"></span>
          </button>
        </div>
      </div>
      <div class="mk-table-overlay-body" [innerHTML]="expandedTableHtml"></div>
    </div>
  </div>
}

<ng-template #sliceImageVisualizer let-value="content" let-message="message" let-index="index">
  <app-slice-image-visualizer [messageContent]="value"
    (contentChange)="onSliceContentChange(message, index, $event)"></app-slice-image-visualizer>
</ng-template>

<ng-template #shotImageVisualizer let-value="content" let-message="message" let-index="index">
  <app-shot-image-visualizer [messageContent]="value"
    (contentChange)="onShotContentChange(message, index, $event)"></app-shot-image-visualizer>
</ng-template>

<ng-template #ragResultVisualizer let-value="content">
  <app-rag-result-visualizer [messageContent]="value"></app-rag-result-visualizer>
</ng-template>
